import { z } from 'zod/v4';

/** Stable, non-sensitive reason codes shared by analysis, API and UI. */
export const REVIEW_REASON_CODES = [
  'ROLE_CONFLICT',
  'UNGROUNDED_CRITICAL_EVIDENCE',
  'FACT_VERIFICATION_FAILED',
  'SAFETY_SIGNAL',
  'UNGROUNDED_SAFETY_SIGNAL',
  'CATEGORY_AMBIGUOUS',
  'CLEANUP_INTEGRITY_FAILED',
  'PII_MINIMIZATION_FAILED',
  'VERIFIER_UNAVAILABLE',
  'REVIEW_METADATA_INVALID',
] as const;

export const humanReviewReasonCodeSchema = z.enum(REVIEW_REASON_CODES);
export const humanReviewStatusSchema = z.enum([
  'pending',
  'approved',
  'rejected',
  'superseded',
]);
export const humanReviewDeliveryStatusSchema = z.enum([
  'not_queued',
  'queued',
  'delivering',
  'sent',
  'failed',
  'rejected',
  'superseded',
]);

/** Evidence safe enough for the operational D1 read model. No transcript/output. */
export const humanReviewEvidenceSchema = z
  .object({
    metadataValid: z.boolean(),
    verificationStatus: z.enum(['verified', 'failed', 'unavailable', 'invalid']),
    provider: z.string().min(1).max(50),
  })
  .strict();

export const humanReviewDecisionSchema = z
  .object({
    id: z.string().min(1),
    reviewId: z.string().uuid(),
    fromStatus: humanReviewStatusSchema,
    toStatus: humanReviewStatusSchema,
    decisionVersion: z.number().int().nonnegative(),
    reviewerId: z.string().min(1).nullable(),
    notes: z.string().max(2000).nullable(),
    createdAt: z.string().datetime(),
  })
  .strict();

export const humanReviewSchema = z
  .object({
    id: z.string().uuid(),
    recordId: z.string().uuid(),
    analysisId: z.string().uuid(),
    orgId: z.string().uuid(),
    projectId: z.string().uuid(),
    actionGroup: z.literal('analysis_automation'),
    reasonCodes: z.array(humanReviewReasonCodeSchema).min(1),
    evidence: humanReviewEvidenceSchema,
    /** Null only for legacy rows created before durable review delivery existed. */
    deliveryId: z.string().uuid().nullable(),
    status: humanReviewStatusSchema,
    deliveryStatus: humanReviewDeliveryStatusSchema,
    deliveryErrorCode: z.string().max(100).nullable(),
    decisionVersion: z.number().int().nonnegative(),
    reviewerId: z.string().min(1).nullable(),
    reviewNotes: z.string().max(2000).nullable(),
    reviewedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    decisions: z.array(humanReviewDecisionSchema),
  })
  .strict();

export const listHumanReviewsQuerySchema = z.object({
  status: z.union([humanReviewStatusSchema, z.literal('all')]).default('pending'),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const humanReviewListResponseSchema = z
  .object({
    reviews: z.array(humanReviewSchema),
    total: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
  })
  .strict();

export const reviewActionSchema = z
  .object({
    notes: z.string().trim().min(1).max(2000),
    expectedDecisionVersion: z.number().int().nonnegative(),
  })
  .strict();

export const retryHumanReviewDeliverySchema = z
  .object({
    expectedDecisionVersion: z.number().int().positive(),
  })
  .strict();

export const humanReviewActionResponseSchema = z
  .object({
    review: humanReviewSchema,
    deliveryQueued: z.boolean(),
  })
  .strict();

/**
 * What the fact verifier can object to in free narrative.
 *
 * Lives here, next to `REVIEW_REASON_CODES`, because three layers read it: the
 * verifier's JSON schema in workflows-api, the persisted analysis output, and
 * the record screen that shows an operator which statements to check.
 */
export const NARRATIVE_ISSUE_TYPES = [
  'money_or_currency',
  'quantity_or_unit',
  'date_or_time',
  'product_or_model',
  'contract_or_payment_state',
  'commitment_or_owner',
  'temporal_scope',
  'safety',
] as const;

export const FACT_ISSUE_REASON_CODES = [
  'DIRECT_SUPPORT',
  'ABSENT',
  'CONTRADICTED',
  'AMBIGUOUS_TEMPORAL_SCOPE',
  'MISSING_UNIT_OR_CURRENCY',
  'OWNER_UNVERIFIED',
  'NORMALIZED_DATE_UNVERIFIED',
  'ENTITY_MISMATCH',
] as const;

/**
 * Словарь НАРЕКАНИЯ к прозе отчёта — только коды, у которых доказательство
 * существует: `CONTRADICTED`, `MISSING_UNIT_OR_CURRENCY`,
 * `NORMALIZED_DATE_UNVERIFIED`, `ENTITY_MISMATCH`.
 *
 * Замер #773 (итерация 3, три прогона, то, что доходит до экрана): из 156
 * ложных нареканий 94 дают `ABSENT` (44 из 66), `OWNER_UNVERIFIED` (29 из 41)
 * и `AMBIGUOUS_TEMPORAL_SCOPE` (21 из 26). Объединяет их не тема, а форма
 * утверждения: каждый заявляет, чего в разговоре НЕТ. Отсутствие не
 * предъявляется цитатой — цитата доказывает наличие, — поэтому такое
 * нарекание нечем проверить ни оператору, ни `isCoherentNarrativeIssue`. Коды,
 * которые обязаны указать на конкретные слова, вдвое точнее (41% ложных против
 * 67–81%). `DIRECT_SUPPORT` («транскрипт утверждает это значение») парен к
 * `status: supported` у вердикта об утверждении; у нарекания поля `status` нет,
 * оно возражение по построению, и «возражаю, потому что подтверждено» —
 * дефект контракта (на проде такие нарекания были: подтверждение, поданное как
 * претензия, 58% нареканий выборки оказались ложными).
 *
 * У ВЕРДИКТОВ ОБ УТВЕРЖДЕНИЯХ снятые коды остаются (`FACT_ISSUE_REASON_CODES`):
 * там есть `status`, и «в транскрипте этого значения нет» — законный ответ о
 * конкретном значении, а не о тексте целиком.
 *
 * Этим словарём держится ТОЛЬКО схема, которой связан провайдер
 * (`FACT_VERIFIER_JSON_SCHEMA`). Приём и хранение намеренно остаются на полном
 * списке `FACT_ISSUE_REASON_CODES`:
 *
 *   - приём (`narrativeIssueSchema` внутри `FactVerifierVerdictSchema`)
 *     проверяет ВЕСЬ ответ разом, поэтому одно нарекание с кодом вне словаря
 *     стоило бы всего вердикта — одна платная ремонтная попытка, затем
 *     `unavailable`, а это снимает с корректного анализа все утверждения для
 *     CRM;
 *   - хранение (`storedNarrativeIssueSchema`) разбирает записи, сделанные до
 *     сужения, и они несут снятые коды (`DIRECT_SUPPORT`, `ABSENT`,
 *     `OWNER_UNVERIFIED`, `AMBIGUOUS_TEMPORAL_SCOPE`). Сузив его, мы потеряли
 *     бы ровно эти нарекания — `analyses.mapper.ts` фильтрует список
 *     ПОЭЛЕМЕНТНО (`rawIssues.filter(safeParse)`), так что состояние проверки
 *     на экране уцелело бы, — но потеряли бы молча, схемой, тогда как решение
 *     «это нарекание не показывается» должно приниматься в одном месте и
 *     называться своим именем.
 *
 * Это место — `isCoherentNarrativeIssue` ниже: его зовёт и запись
 * (`checkNarrativeIssues`, workflows-api), и чтение (`analyses.mapper.ts`),
 * поэтому старая запись перестаёт показываться, не дожидаясь переанализа.
 */
export const NARRATIVE_ISSUE_REASON_CODES = [
  'CONTRADICTED',
  'MISSING_UNIT_OR_CURRENCY',
  'NORMALIZED_DATE_UNVERIFIED',
  'ENTITY_MISMATCH',
] as const;

/** What the verifier may RETURN — the same 500 its JSON schema declares. */
export const VERIFIER_QUOTE_MAX_LENGTH = 500;

/**
 * What we may STORE, deliberately longer.
 *
 * The stored value is not the model's string: it is the span of the transcript
 * that string matched, and the match runs on whitespace-collapsed text, so the
 * source span can be longer than what was compared. The headroom is for that
 * difference — a span that still exceeds it is clipped at a word boundary with
 * an ellipsis, so a clipped quote reads as clipped.
 */
export const NARRATIVE_QUOTE_MAX_LENGTH = 600;

/**
 * One objection AS THE VERIFIER RETURNED IT: which section of the report, what
 * kind of fact, the drafted wording and the transcript quote it weighed the
 * wording against.
 *
 * This is the runtime half of the provider contract — `FACT_VERIFIER_JSON_SCHEMA`
 * is what the provider is held to, this is what we accept afterwards, and the
 * two must agree. A quote longer than the schema advertises has to fail here,
 * or the invalid-verdict path (one repair attempt, then `unavailable`) stops
 * being reachable. What we PERSIST has its own schema below, because it holds
 * transcript text rather than model text.
 */
export const narrativeIssueSchema = z
  .object({
    sectionId: z.string().min(1).max(120),
    issueType: z.enum(NARRATIVE_ISSUE_TYPES),
    draftExcerpt: z.string().min(1).max(500),
    evidenceQuote: z.string().max(VERIFIER_QUOTE_MAX_LENGTH),
    reasonCode: z.enum(FACT_ISSUE_REASON_CODES),
  })
  .strict();

/**
 * One objection AS PERSISTED AND SHOWN: same shape, but the quote may be a
 * longer transcript span than the model's string was allowed to be.
 *
 * Quotes here are minimized derived output — see the persistence comment in
 * `finalize-verification-step.ts`.
 */
export const storedNarrativeIssueSchema = narrativeIssueSchema
  .extend({
    evidenceQuote: z.string().max(NARRATIVE_QUOTE_MAX_LENGTH),
  })
  .strict();

/**
 * Показывается ли нарекание: годится ли оно как возражение.
 *
 * Один предикат на оба пути — запись (`checkNarrativeIssues`) и чтение
 * (`analyses.mapper.ts`), — потому что иначе появился бы второй решающий:
 * экран показывал бы то, что конвейер уже признал браком.
 *
 * Правило (#773, v7) — два условия вместе:
 *
 *   - КОД из словаря нарекания (`NARRATIVE_ISSUE_REASON_CODES`). Остальное —
 *     `DIRECT_SUPPORT`, `ABSENT`, `OWNER_UNVERIFIED`,
 *     `AMBIGUOUS_TEMPORAL_SCOPE` — брак по построению: «подтверждено» не
 *     возражение, а «в разговоре этого нет» цитатой не предъявить.
 *   - НЕПУСТАЯ цитата — у каждого оставшегося кода. `CONTRADICTED` и
 *     `ENTITY_MISMATCH` утверждают, что в разговоре сказано ИНОЕ, а
 *     `MISSING_UNIT_OR_CURRENCY` и `NORMALIZED_DATE_UNVERIFIED` — что слова
 *     разговора и формулировка отчёта расходятся; без слов, на которые можно
 *     показать, читателю нечем сверяться и приходится верить на слово.
 *
 * Записи, сделанные по прежним правилам (например, `ABSENT` с пустой цитатой
 * или `MISSING_UNIT_OR_CURRENCY` без цитаты), перестают показываться при
 * чтении, не дожидаясь переанализа.
 *
 * **Предикат судит о том, что вернул ВЕРИФИКАТОР, — и пустая цитата говорит
 * о верификаторе только до PII-границы.** `evidenceQuote` входит в
 * `DERIVED_EVIDENCE_KEYS`, а `finalizeAnalysis` прогоняет
 * `minimizeDerivedOutput` по всему выводу вместе с `analysisVerification`
 * (`finalize-verification-step.ts`): цитата с неклассифицируемой цифровой
 * последовательностью обнуляется ЦЕЛИКОМ уже после того, как нарекание принято.
 * Поэтому у лежащей записи пустая цитата неотличима от изъятой ради
 * приватности, и требование цитаты там снимается для ВСЕХ оставшихся кодов —
 * `options.evidenceMayBeRedacted`. Иначе законное нарекание, чья цитата
 * содержала номер заказа, исчезало бы с экрана и из печатного отчёта молча:
 * потерять нарекание, требующее проверки, дороже, чем показать его без цитаты.
 * Послабление не касается словаря: код вне него остаётся браком всегда.
 *
 * **Флаг — не константа чтения, а факт об анализе.** Изъятие оставляет след:
 * `unclassifiedEvidenceCount > 0` в `finalizeAnalysis` даёт
 * `PII_MINIMIZATION_FAILED` в `reviewReasonCodes`, и чтение передаёт сюда
 * именно его (`analyses.mapper.ts`). Включать послабление всегда значило бы
 * перестать скрывать записи с пустой цитатой, ради которых проверка на чтении
 * и заведена. Признак относится к анализу, а не к отдельному нареканию, —
 * оценка сверху, но узкая: там, где изъятия не было, правило остаётся строгим.
 * На записи послабление не действует: `checkNarrativeIssues` зовёт предикат
 * без флага, и цитата там обязательна — минимизация идёт позже.
 *
 * Минимизатор не меняет `reasonCode` и не ДОБАВЛЯЕТ цитату, поэтому проверка
 * кода по обе стороны границы одна и та же.
 *
 * Сужает тип: у пережившего проверку нарекания `reasonCode` из словаря
 * нарекания, и компилятор несёт это до границы хранения и экрана.
 */
export function isCoherentNarrativeIssue(
  issue: StoredNarrativeIssue,
  options: { evidenceMayBeRedacted?: boolean } = {}
): issue is StoredNarrativeIssue & { reasonCode: NarrativeIssueReasonCode } {
  // Код вне словаря нарекания — брак по построению: `DIRECT_SUPPORT`
  // («транскрипт утверждает это значение») и три кода, снятые итерацией 4 как
  // непроверяемые. Проверка по списку, а не перечислением: добавить код в
  // словарь и забыть про эту ветку теперь невозможно.
  if (!(NARRATIVE_ISSUE_REASON_CODES as readonly string[]).includes(issue.reasonCode)) {
    return false;
  }
  // Единственное оставшееся требование — доказательство. Возражение без слов,
  // на которые можно показать, читателю проверить нечем: он видит «тут ошибка»
  // и обязан поверить на слово. На слово верификатор прав примерно в трёх
  // случаях из десяти (замер #773).
  //
  // Исключение — след PII-минимизации: она обнуляет цитату УЖЕ ПОСЛЕ проверки
  // (`minimizeDerivedOutput`), и без этой ветки законное нарекание исчезало бы
  // с экрана из-за собственной защиты данных. Флаг выводится из
  // `PII_MINIMIZATION_FAILED` в метаданных анализа, а не ставится константой:
  // иначе он снял бы требование и с записей, где изъятия не было.
  return options.evidenceMayBeRedacted === true || issue.evidenceQuote !== '';
}

/**
 * The verification state of one analysis, as the record screen needs it.
 *
 * `narrativeIssues` is empty for analyses stored before the verdict was
 * persisted (they carried only a count) and for a verifier that never returned
 * one — so an empty list means "nothing to show", never "nothing was found".
 * Read it together with `verificationStatus`.
 */
export const analysisVerificationStateSchema = z
  .object({
    verificationStatus: z.enum(['verified', 'review_required', 'unavailable']),
    reviewReasonCodes: z.array(humanReviewReasonCodeSchema),
    narrativeIssues: z.array(storedNarrativeIssueSchema),
  })
  .strict();

export type NarrativeIssueType = (typeof NARRATIVE_ISSUE_TYPES)[number];
export type FactIssueReasonCode = (typeof FACT_ISSUE_REASON_CODES)[number];
export type NarrativeIssueReasonCode = (typeof NARRATIVE_ISSUE_REASON_CODES)[number];
export type NarrativeIssue = z.infer<typeof narrativeIssueSchema>;
export type StoredNarrativeIssue = z.infer<typeof storedNarrativeIssueSchema>;
export type AnalysisVerificationState = z.infer<typeof analysisVerificationStateSchema>;
export type HumanReviewReasonCode = z.infer<typeof humanReviewReasonCodeSchema>;
export type HumanReviewStatus = z.infer<typeof humanReviewStatusSchema>;
export type HumanReviewDeliveryStatus = z.infer<typeof humanReviewDeliveryStatusSchema>;
export type HumanReviewEvidence = z.infer<typeof humanReviewEvidenceSchema>;
export type HumanReviewDecision = z.infer<typeof humanReviewDecisionSchema>;
export type HumanReview = z.infer<typeof humanReviewSchema>;
export type ListHumanReviewsQuery = z.infer<typeof listHumanReviewsQuerySchema>;
export type HumanReviewListResponse = z.infer<typeof humanReviewListResponseSchema>;
export type ReviewAction = z.infer<typeof reviewActionSchema>;
export type RetryHumanReviewDelivery = z.infer<typeof retryHumanReviewDeliverySchema>;
export type HumanReviewActionResponse = z.infer<typeof humanReviewActionResponseSchema>;
